import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import useIsMobile from 'hooks/useIsMobile';
import useIsFeatureAvailable from 'hooks/useIsFeatureAvailable';
import { logErrors, removeFromStorage } from 'utils';
import { STORAGE_KEYS } from 'utils/constants';
import { BRAND_CONFIG_MAPPING } from 'utils/brand-config';
import { SERVICES } from 'utils/service-entitlements-config';
import { selectAccessToken } from 'services/session/sessionSelectors';
import { getUserIdFromAccessToken } from 'services/session/utils';
import { fetchUserDetails } from 'services/user/userAsync';
import { selectUserId } from 'services/user/userSelectors';
import {
  selectOfferPlanCode,
  selectOfferGroupCode,
  selectIsOfferDetailsExist,
} from 'services/offer/offerSelectors';
import { getOfferDetails } from 'services/offer/offerAsync';
import {
  selectIsConversionEligibilityDetailsExist,
  selectIsBillingAccountDetailsExist,
  selectIsAvailableOffersFetched,
} from 'services/ctp/ctpSelectors';
import {
  fetchConversionEligibility,
  fetchAvailableOffers,
  fetchBillingAccountInfo,
} from 'services/ctp/ctpAsync';
import {
  selectBrandCode,
  selectIsCompanyDetailsExist,
  selectBrandVaultProject,
} from 'services/brand/brandSelectors';
import { fetchCompanyCodeDetails } from 'services/brand/brandAsync';
import { findPlanDetails } from 'services/plan/planAsync';
import { selectIsPlanDetailsExist } from 'services/plan/planSelectors';
import { fetchUserAlerts } from 'services/alerts/alertsAsync';
import {
  selectAlerts,
  selectAlertsMetadata,
} from 'services/alerts/alertsSelectors';
import { initWorker } from 'services/vault/sdkFunctions';

function useSessionInitialSetup() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isMobile = useIsMobile();
  const hasAlerts = useIsFeatureAvailable(SERVICES.alerts);

  const alerts = useAppSelector(selectAlerts);
  const alertsMetadata = useAppSelector(selectAlertsMetadata);
  const userId = useAppSelector(selectUserId);
  const brandCode = useAppSelector(selectBrandCode);
  const userAccessToken = useAppSelector(selectAccessToken);
  const vaultProject = useAppSelector(selectBrandVaultProject);
  const isOfferDetailsExist = useAppSelector(selectIsOfferDetailsExist);
  const isConversionEligibilityDetailsExist = useAppSelector(
    selectIsConversionEligibilityDetailsExist,
  );
  const isBillingAccountDetailsExist = useAppSelector(
    selectIsBillingAccountDetailsExist,
  );
  const isAvailableOffersFetched = useAppSelector(
    selectIsAvailableOffersFetched,
  );
  const offerPlanCode = useAppSelector(selectOfferPlanCode);
  const groupCode = useAppSelector(selectOfferGroupCode);
  const isCompanyDetailsExist = useAppSelector(selectIsCompanyDetailsExist);
  const isPlanDetailsExist = useAppSelector(selectIsPlanDetailsExist);

  /* Fetch user details if not exist */
  useEffect(() => {
    async function getUserDetails(token: string) {
      try {
        const identifier = getUserIdFromAccessToken(token);
        await dispatch(fetchUserDetails(identifier));
      } catch (error) {
        logErrors(error);
      }
    }

    if (userAccessToken && !userId) {
      getUserDetails(userAccessToken);
    }
  }, [userAccessToken, userId]);

  /* Fetch user offer details if not exist */
  useEffect(() => {
    async function getUserOfferDetails() {
      try {
        await dispatch(getOfferDetails());
      } catch (error) {
        logErrors(error);
      }
    }
    if (!isOfferDetailsExist && userId) {
      getUserOfferDetails();
    }
  }, [userId, isOfferDetailsExist]);

  /* Fetch user conversion elgibilty details if not exist */
  useEffect(() => {
    async function getConversionEligibility() {
      try {
        await dispatch(fetchConversionEligibility());
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isConversionEligibilityDetailsExist && userId) {
      getConversionEligibility();
    }
  }, [userId, isConversionEligibilityDetailsExist]);

  /* Fetch user billing account details if not exist */
  useEffect(() => {
    async function getUserBillingAccountDetails() {
      try {
        await dispatch(fetchBillingAccountInfo());
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isBillingAccountDetailsExist && userId) {
      getUserBillingAccountDetails();
    }
  }, [userId, isBillingAccountDetailsExist]);

  /* Fetch available offer details if not exist */
  useEffect(() => {
    async function getAvailableOffers() {
      try {
        await dispatch(fetchAvailableOffers());
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isAvailableOffersFetched && userId) {
      getAvailableOffers();
    }
  }, [userId, isAvailableOffersFetched]);

  /* Fetch the user company details if not exist */
  useEffect(() => {
    async function getCompanyDetails(companyCode: string) {
      try {
        await dispatch(fetchCompanyCodeDetails(companyCode));
      } catch (error) {
        logErrors(error);
      }
    }

    if (
      BRAND_CONFIG_MAPPING[brandCode].hasCustomerServiceSupport &&
      !isCompanyDetailsExist &&
      groupCode
    ) {
      getCompanyDetails(groupCode);
    }
  }, [groupCode, isCompanyDetailsExist, brandCode]);

  /* Fetch the user plan details if not exist */
  useEffect(() => {
    async function getPlanDetails(planCode: string) {
      try {
        await dispatch(findPlanDetails(planCode));
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isPlanDetailsExist && offerPlanCode) {
      getPlanDetails(offerPlanCode);
    }
  }, [offerPlanCode, isPlanDetailsExist]);

  // fetch alerts on page load
  useEffect(() => {
    async function getUserAlerts() {
      try {
        await dispatch(fetchUserAlerts(alertsMetadata.page));
      } catch (error) {
        logErrors(error);
      }
    }
    if (userId && hasAlerts && !alerts.length) {
      getUserAlerts();
    }
  }, [userId, hasAlerts]);

  /* update main content margin on reload and resize except dashboard page */
  useEffect(() => {
    const handleWindowResize = () => {
      const documentWidth = document.documentElement.clientWidth;
      const leftNavElement = document.getElementById('left-nav');
      const mainContentElement = document.getElementById('main-content');

      if (mainContentElement && leftNavElement) {
        const leftNavWidth = leftNavElement.clientWidth || 300;
        const contentWidth = 600;
        const remainingWidth = (documentWidth - contentWidth) / 2;
        const margin = remainingWidth - leftNavWidth - 60; // 30 is the right padding of the sidebar + 30 is the left padding of the main content
        mainContentElement.style.setProperty(
          '--margin-left',
          !isMobile && window.location.pathname !== '/dashboard'
            ? `${margin}px`
            : '0px',
        );
      }
    };

    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);
    removeFromStorage(STORAGE_KEYS.inactivity); // remove inactivity session on reload

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [location]);

  /* Init vault worker */
  useEffect(() => {
    const terminate =
      vaultProject && userId
        ? initWorker({ project: vaultProject, userId })
        : () => null;

    return terminate;
  }, [vaultProject, userId]);
}

export default useSessionInitialSetup;
